<template>
  <div :style="item.style" :class="item.class">
    <!-- Full Screen Calendar View -->
    <div class="fullscreen-calendar-container">
      <MonthlyCalendar
        :key="calendarKey"
        :pages="listData"
        :loading="loading"
        @event-click="handleEventClick"
        @add-event="handleAddEvent"
        @edit-event="handleEditEvent"
        @show-more="showMoreEvents"
        @month-change="handleMonthChange"
        @immediate-move="handleImmediateMove"
        @event-drag="handleEventDrag"
      />
    </div>

    <!-- Event Form Modal (when editing) -->
    <div v-if="showEventForm" class="event-form-overlay" @click="closeEventForm">
      <div class="event-form-modal" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditingEvent ? 'Edit Event' : 'Add New Event' }}
          </h5>
          <button @click="closeEventForm" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <GoogleEventForm
            :initial-data="selectedItem"
            :is-editing="isEditingEvent"
            @submit="onFormSubmit"
            @cancel="closeEventForm"
            @delete="onFormDelete"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonthlyCalendar from "./MonthlyCalendar.vue";
import GoogleEventForm from "./GoogleEventForm.vue";
import * as categoryService from "../../service/category.service";
import api from "../../../api";

export default {
  name: "PCNEventsTab",
  props: {
    item: {
      type: Object,
      required: true,
    },
    setTopbarControls: {
      type: Function,
      required: false,
    },
    topbarControls: {
      type: Object,
      required: false,
    },
  },
  components: {
    MonthlyCalendar,
    GoogleEventForm,
  },
  data() {
    return {
      listData: [],
      selectedItem: null,
      loading: false,
      showEventForm: false,
      isEditingEvent: false,
      calendarKey: 0,
    };
  },
  computed: {
    totalItems() {
      return this.listData.length;
    },
    totalActiveItems() {
      return this.listData.filter(x => x.active === true).length;
    },
  },
  watch: {
    topbarControls: {
      handler: function (val) {
        if (val && val.title === "Events") {
          this.handleTopbarControls(val);
        }
      },
      deep: true,
    },
  },
  methods: {
    async loadEvents() {
      this.loading = true;
      try {
        // Use the pages API to load events for the current category
        const categoryId = this.$route.params.categoryId;
        if (!categoryId) {
          console.warn('No category ID found in route params');
          return;
        }

        const response = await categoryService.getPages(categoryId);
        
        // Filter and sort events
        this.listData = (response || [])
          .filter(event => !event.deleted) // Exclude deleted events
          .sort((a, b) => {
            // Sort by date, then by start time
            const dateA = a.additionalProp1?.date || a.date || '9999-12-31';
            const dateB = b.additionalProp1?.date || b.date || '9999-12-31';
            
            if (dateA !== dateB) {
              return dateA.localeCompare(dateB);
            }
            
            const timeA = a.additionalProp1?.start_time || a.start_time || '00:00';
            const timeB = b.additionalProp1?.start_time || b.start_time || '00:00';
            return timeA.localeCompare(timeB);
          });
        
        console.log(`Loaded ${this.listData.length} events for category ${categoryId}`);
      } catch (error) {
        console.error('Error loading events:', error);
        this.listData = [];
        this.showErrorMessage('Load Failed', 'Failed to load events. Please refresh the page.');
      } finally {
        this.loading = false;
      }
    },

    selectEvent(event) {
      this.selectedItem = {
        ...event,
        // Ensure time fields are properly mapped
        start_time: event.start_time || event.additionalProp1?.start_time || '',
        end_time: event.end_time || event.additionalProp1?.end_time || '',
        date: event.date || event.additionalProp1?.date || '',
      };
    },

    handleTopbarControls(controls) {
      // Handle add new event
      if (controls.addNew) {
        this.createNewEvent(controls.addNewData);
      }
      
      // Handle delete event
      if (controls.delete && this.selectedItem) {
        this.deleteEvent(this.selectedItem.id);
      }
      
      // Handle toggle active status
      if (controls.toggleActive && this.selectedItem) {
        this.toggleEventStatus(this.selectedItem.id);
      }
    },

    async createNewEvent(eventData) {
      try {
        // Generate unique identifiers
        const uuid = this.generateUuid();
        const defaultSlug = eventData.title.replace(/\s/g, "").toLowerCase();
        
        // Get current practice context
        const currentPractice = this.$store.getters.currentPractice || {};
        const currentCms = this.$store.getters.currentCms || {};
        
        // Prepare the page data structure to match the working curl request
        const pageData = {
          uuid: uuid,
          url: currentPractice.additionalProp1?.url || '',
          active: eventData.active !== undefined ? eventData.active : true,
          title: eventData.title,
          picture: "",
          excerpt: "NA", // Match the curl request format
          description: eventData.description || "",
          deleted: false,
          creationDate: new Date().toISOString(),
          lastModifiedDate: new Date().toISOString(),
          cmsId: currentCms.id || this.$route.params.cmsId,
          additionalProp1: {
            dynamoId: currentPractice.additionalProp1?.dynamoId || currentPractice.additionalProp1?.url?.split('.')[0] || '',
            date: eventData.date || eventData.additionalProp1?.date || '',
            start_time: eventData.start_time || eventData.additionalProp1?.start_time || '',
            end_time: eventData.end_time || eventData.additionalProp1?.end_time || '',
            end_date: eventData.additionalProp1?.end_date || eventData.date,
            description: eventData.description || eventData.additionalProp1?.description || '',
            color: eventData.additionalProp1?.color || '#1a73e8',
            all_day: eventData.additionalProp1?.all_day || false,
            slug: defaultSlug,
            categoryName: this.$route.params.categoryName || 'Events'
          }
        };

        // Create the page using the correct service method
        const response = await categoryService.postPages(this.$route.params.categoryId, pageData);
        
        if (response) {
          // Create slug for the page
          await this.createSlug(defaultSlug, response, currentPractice, currentCms.id);
          
          // Background refresh the created event to get complete data
          await this.refreshSingleEvent(response.id);
          
          this.$emit('event-created', response);
          this.showSuccessMessage('Event Created!', 'Event created successfully!');
        }
      } catch (error) {
        console.error('Error creating event:', error);
        this.showErrorMessage('Creation Failed', 'Failed to create event. Please try again.');
      }
    },

    async createSlug(slug, page, currentPractice, cmsId) {
      try {
        const slugData = {
          uuid: this.generateUuid(),
          name: slug,
          url: (currentPractice.additionalProp1?.url || '') + "/" + slug,
          path: "/" + slug,
          creationDate: new Date().toISOString(),
          lastModifiedDate: new Date().toISOString(),
          pageId: page.id,
          cmsId: cmsId,
          alertId: "",
          additionalProp1: {}
        };
        
        await api.post(`/api/pages/${page.id}/slug`, slugData);
      } catch (error) {
        console.error('Error creating slug:', error);
        // Don't throw here as the page was created successfully
      }
    },

    async deleteEvent(eventId) {
      try {
        // Find the event to get its title for confirmation
        const event = this.listData.find(e => e.id === eventId);
        const eventTitle = event ? event.title : 'this event';

        // Show confirmation dialog
        let confirmed = true;
        if (window.Swal) {
          const result = await window.Swal.fire({
            title: `Delete ${eventTitle}?`,
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
          });
          confirmed = result.isConfirmed;
        }

        if (!confirmed) return;

        // Delete the event - match the format from meetings-events component
        const params = {
          categoryName: this.$route.params.categoryName,
          slug: event.additionalProp1?.slug || '',
        };
        await api.delete(`/api/pages/${eventId}`, { params });
        
        // Smoothly remove the event from calendar
        await this.removeEventFromCalendar(eventId);
        
        // Close form if this event was selected
        if (this.selectedItem && this.selectedItem.id === eventId) {
          this.closeEventForm();
        }
        
        this.$emit('event-deleted', eventId);
        this.showSuccessMessage('Event Deleted!', 'Event deleted successfully!');
      } catch (error) {
        console.error('Error deleting event:', error);
        this.showErrorMessage('Delete Failed', 'Failed to delete event. Please try again.');
      }
    },

    async toggleEventStatus(eventId) {
      try {
        const event = this.listData.find(e => e.id === eventId);
        if (event) {
          const newStatus = !event.active;
          await api.patch(`/api/pages/${eventId}`, { active: newStatus });
          
          // Update local data
          event.active = newStatus;
          if (this.selectedItem && this.selectedItem.id === eventId) {
            this.selectedItem.active = newStatus;
          }
        }
      } catch (error) {
        console.error('Error toggling event status:', error);
      }
    },

    onFormSubmit(formData) {
      if (this.isEditingEvent) {
        this.updateEvent(formData);
      } else {
        this.createNewEvent(formData);
      }
      this.closeEventForm();
    },

    onFormDelete() {
      if (this.selectedItem) {
        this.deleteEvent(this.selectedItem.id);
      }
    },

    onFormUpdate(formData) {
      // Handle real-time form updates if needed
      if (this.selectedItem) {
        Object.assign(this.selectedItem, formData);
      }
    },

    async updateEvent(formData) {
      if (!this.selectedItem) return;
      
      try {
        // Prepare update data with proper structure - preserve all existing properties
        const updateData = {
          title: formData.title,
          description: formData.description || '',
          active: formData.active !== undefined ? formData.active : true,
          excerpt: formData.description ? formData.description.substring(0, 150) : "",
          lastModifiedDate: new Date().toISOString(),
          additionalProp1: {
            // Preserve all existing additionalProp1 properties
            ...this.selectedItem.additionalProp1,
            // Override with new form data
            date: formData.date || formData.additionalProp1?.date || this.selectedItem.additionalProp1?.date,
            start_time: formData.start_time || formData.additionalProp1?.start_time || this.selectedItem.additionalProp1?.start_time || '',
            end_time: formData.end_time || formData.additionalProp1?.end_time || this.selectedItem.additionalProp1?.end_time || '',
            end_date: formData.additionalProp1?.end_date || formData.date || this.selectedItem.additionalProp1?.end_date,
            description: formData.description || formData.additionalProp1?.description || this.selectedItem.additionalProp1?.description || '',
            color: formData.additionalProp1?.color || formData.color || this.selectedItem.additionalProp1?.color || '#1a73e8',
            all_day: formData.additionalProp1?.all_day !== undefined ? formData.additionalProp1.all_day : (this.selectedItem.additionalProp1?.all_day || false),
            slug: this.selectedItem.additionalProp1?.slug || formData.title.replace(/\s/g, "").toLowerCase(),
            categoryName: this.$route.params.categoryName || 'Events'
          }
        };

        // Immediately update UI for responsive feedback
        this.updateEventInUI(this.selectedItem.id, updateData);
        
        const response = await api.patch(`/api/pages/${this.selectedItem.id}`, updateData);
        
        if (response.data) {
          // Background refresh the updated event to get complete data  
          await this.refreshSingleEvent(this.selectedItem.id);
          
          // Update selected item with refreshed data
          const refreshedEvent = this.listData.find(e => e.id === this.selectedItem.id);
          if (refreshedEvent) {
            this.selectedItem = { ...refreshedEvent };
          }
          
          this.$emit('event-updated', response.data);
          this.showSuccessMessage('Event Updated!', 'Event updated successfully!');
        } else {
          // Revert UI changes if API call failed
          await this.refreshSingleEvent(this.selectedItem.id);
        }
      } catch (error) {
        console.error('Error updating event:', error);
        this.showErrorMessage('Update Failed', 'Failed to update event. Please try again.');
        
        // Revert UI changes on error by refreshing from server
        await this.refreshSingleEvent(this.selectedItem.id);
      }
    },

    // Utility methods
    generateUuid() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },

    showSuccessMessage(title, message) {
      console.log(`SUCCESS: ${title} - ${message}`);
      
      if (window.Swal) {
        window.Swal.fire({
          title: title,
          text: message,
          icon: 'success',
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: false,
          backdrop: true,
          customClass: {
            popup: 'swal-centered-popup'
          }
        });
      }
    },

    showErrorMessage(title, message) {
      console.error(`ERROR: ${title} - ${message}`);
      
      if (window.Swal) {
        window.Swal.fire({
          title: title,
          text: message,
          icon: 'error',
          position: 'top',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          backdrop: true,
          customClass: {
            popup: 'swal-centered-popup'
          }
        });
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatTime(startTime, endTime) {
      const times = [];
      if (startTime) times.push(startTime);
      if (endTime) times.push(endTime);
      return times.join(' - ') || 'No time set';
    },

    // Calendar event handlers
    handleEventClick(event) {
      this.isEditingEvent = true;
      this.selectedItem = {
        ...event,
        // Ensure time fields are properly mapped
        start_time: event.start_time || event.additionalProp1?.start_time || '',
        end_time: event.end_time || event.additionalProp1?.end_time || '',
        date: event.date || event.additionalProp1?.date || '',
      };
      this.showEventForm = true;
    },

    handleAddEvent(eventData) {
      this.isEditingEvent = false;
      this.selectedItem = {
        title: '',
        description: '',
        date: eventData?.date || new Date().toISOString().split('T')[0],
        start_time: eventData?.time || '',
        end_time: '',
        active: true,
        additionalProp1: {
          date: eventData?.date || new Date().toISOString().split('T')[0],
          start_time: eventData?.time || '',
          end_time: '',
          description: ''
        }
      };
      this.showEventForm = true;
    },

    handleEditEvent(event) {
      this.handleEventClick(event);
    },

    showMoreEvents(dateInfo) {
      // Handle showing more events for a specific date
      console.log('Show more events for:', dateInfo);
      // You could open a modal or expand the date cell
    },

    handleMonthChange(monthInfo) {
      // Handle month navigation
      console.log('Month changed to:', monthInfo);
      // You could reload events for the new month if needed
    },

    handleImmediateMove(dragData) {
      console.log('IMMEDIATE MOVE:', dragData);
      
      try {
        const { event, newDate } = dragData;
        
        // Immediately move the element visually
        const index = this.listData.findIndex(e => e.id === event.id);
        if (index > -1) {
          // Create updated event with new date for immediate display
          const movedEvent = {
            ...this.listData[index],
            date: newDate,
            additionalProp1: {
              ...this.listData[index].additionalProp1,
              date: newDate,
              end_date: newDate
            }
          };
          
          // Remove from original position
          this.listData.splice(index, 1);
          
          // Insert in new position (sorted by date)
          const insertIndex = this.findInsertPosition(movedEvent);
          this.listData.splice(insertIndex, 0, movedEvent);
          
          // Force Vue reactivity
          this.listData = [...this.listData];
          
          // Update calendar key for immediate re-render
          this.calendarKey += 1;
          
          console.log('Event moved immediately in UI');
        }
      } catch (error) {
        console.error('Error in immediate move:', error);
      }
    },

    async handleEventDrag(dragData) {
      console.log('HANDLE EVENT DRAG:', dragData);
      
      try {
        const { event, newDate } = dragData;
        
        // Update the event's date while preserving the time
        const updateData = {
          title: event.title,
          description: event.description || '',
          active: event.active,
          lastModifiedDate: new Date().toISOString(),
          additionalProp1: {
            ...event.additionalProp1,
            date: newDate,
            end_date: newDate // Also update end date
          }
        };

        console.log('UPDATE DATA:', updateData);

        // Make API call to update the event
        const response = await api.patch(`/api/pages/${event.id}`, updateData);
        
        if (response.data) {
          // Remove the temporary moved event and apply the fresh API response
          await this.replaceWithApiResponse(event.id, response.data, newDate);
          
          this.showSuccessMessage('Event Moved!', `Event moved to ${newDate}`);
        }
      } catch (error) {
        console.error('Error moving event:', error);
        this.showErrorMessage('Move Failed', 'Failed to move event. Please try again.');
        
        // On error, revert to original position by reloading events
        await this.loadEvents();
      }
    },

    async refreshSingleEvent(eventId, newDate) {
      try {
        // Fetch the updated event record from the API
        const response = await api.get(`/api/pages/${eventId}`);
        
        if (response.data) {
          const updatedEvent = response.data;
          const index = this.listData.findIndex(e => e.id === eventId);
          
          // Prepare refreshed event data
          const refreshedEvent = {
            ...updatedEvent,
            // Ensure date fields are properly set for calendar display
            date: updatedEvent.additionalProp1?.date || newDate || updatedEvent.date,
            additionalProp1: {
              ...updatedEvent.additionalProp1,
              date: updatedEvent.additionalProp1?.date || newDate || updatedEvent.date,
              end_date: updatedEvent.additionalProp1?.end_date || newDate || updatedEvent.date
            }
          };
          
          if (index > -1) {
            // Update existing event: remove old and insert updated
            this.listData.splice(index, 1);
            const insertIndex = this.findInsertPosition(refreshedEvent);
            this.listData.splice(insertIndex, 0, refreshedEvent);
          } else {
            // New event: insert in correct position
            const insertIndex = this.findInsertPosition(refreshedEvent);
            this.listData.splice(insertIndex, 0, refreshedEvent);
          }
          
          // Force Vue reactivity with a new array reference
          this.listData = [...this.listData];
          
          // Increment calendar key to trigger re-render without glitch
          this.calendarKey += 1;
          
          // Wait for DOM update
          await this.$nextTick();
          
          console.log('Event refreshed successfully in calendar');
        }
      } catch (error) {
        console.error('Error refreshing single event:', error);
        // Fallback to full reload if single refresh fails
        await this.loadEvents();
      }
    },

    async removeEventFromCalendar(eventId) {
      try {
        const index = this.listData.findIndex(event => event.id === eventId);
        if (index > -1) {
          // Smoothly remove the event
          this.listData.splice(index, 1);
          
          // Force Vue reactivity with a new array reference  
          this.listData = [...this.listData];
          
          // Update calendar key to trigger re-render
          this.calendarKey += 1;
          
          // Wait for DOM update
          await this.$nextTick();
          
          console.log('Event removed successfully from calendar');
        }
      } catch (error) {
        console.error('Error removing event from calendar:', error);
      }
    },

    updateEventInUI(eventId, updateData) {
      try {
        const index = this.listData.findIndex(e => e.id === eventId);
        if (index > -1) {
          // Immediately update the event in the UI for responsive feedback
          this.listData[index] = {
            ...this.listData[index],
            ...updateData,
            additionalProp1: {
              ...this.listData[index].additionalProp1,
              ...updateData.additionalProp1
            }
          };
          
          // Force Vue reactivity with a new array reference
          this.listData = [...this.listData];
          
          // Update calendar key to trigger immediate re-render
          this.calendarKey += 1;
          
          console.log('Event updated immediately in UI');
        }
      } catch (error) {
        console.error('Error updating event in UI:', error);
      }
    },

    async replaceWithApiResponse(eventId, apiResponseData, newDate) {
      try {
        console.log('REPLACE WITH API RESPONSE:', { eventId, apiResponseData, newDate });
        
        // Find and remove the temporary moved event
        const index = this.listData.findIndex(e => e.id === eventId);
        if (index > -1) {
          this.listData.splice(index, 1);
        }
        
        // Create the final event with API response data
        const finalEvent = {
          ...apiResponseData,
          // Ensure date fields are properly set
          date: apiResponseData.additionalProp1?.date || newDate,
          additionalProp1: {
            ...apiResponseData.additionalProp1,
            date: apiResponseData.additionalProp1?.date || newDate,
            end_date: apiResponseData.additionalProp1?.end_date || newDate
          }
        };
        
        // Insert the final event in the correct position
        const insertIndex = this.findInsertPosition(finalEvent);
        this.listData.splice(insertIndex, 0, finalEvent);
        
        // Force Vue reactivity with new array reference
        this.listData = [...this.listData];
        
        // Update calendar key to trigger re-render
        this.calendarKey += 1;
        
        // Wait for DOM update
        await this.$nextTick();
        
        console.log('Event replaced with API response successfully');
      } catch (error) {
        console.error('Error replacing with API response:', error);
        // Fallback to full reload
        await this.loadEvents();
      }
    },

    findInsertPosition(newEvent) {
      // Find the correct position to insert the event based on date and time sorting
      const newEventDate = newEvent.additionalProp1?.date || newEvent.date || '9999-12-31';
      const newEventTime = newEvent.additionalProp1?.start_time || newEvent.start_time || '00:00';
      
      for (let i = 0; i < this.listData.length; i++) {
        const currentEventDate = this.listData[i].additionalProp1?.date || this.listData[i].date || '9999-12-31';
        const currentEventTime = this.listData[i].additionalProp1?.start_time || this.listData[i].start_time || '00:00';
        
        // Compare dates first
        if (newEventDate < currentEventDate) {
          return i;
        } else if (newEventDate === currentEventDate) {
          // If same date, compare times
          if (newEventTime < currentEventTime) {
            return i;
          }
        }
      }
      
      // If no position found, insert at the end
      return this.listData.length;
    },

    // Modal methods
    closeEventForm() {
      this.showEventForm = false;
      this.selectedItem = null;
      this.isEditingEvent = false;
    },

    saveEvent() {
      // Trigger the form submission
      if (this.isEditingEvent) {
        this.updateEvent(this.selectedItem);
      } else {
        this.createNewEvent(this.selectedItem);
      }
    },
  },

  async mounted() {
    await this.loadEvents();
  },
};
</script>

<style scoped>
.border-right {
  border-right: 1px solid #dadce3 !important;
}

.list-item {
  transition: background-color 0.2s ease;
}

.list-item:hover {
  background-color: #f8f9fa !important;
}

.cursor-pointer {
  cursor: pointer;
}

.list-container {
  border-right: 1px solid #dadce3;
}

.bg-light {
  background-color: #e3f2fd !important;
}

.fullscreen-calendar-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  height: fit-content;
  min-height: 800px;
  width: 100%;
  overflow: visible;
}

/* Event Form Modal */
.event-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.event-form-modal {
  background: white;
  border-radius: 8px;
  max-width: 800px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  position: relative;
  z-index: 1051;
}

.event-form-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.event-form-modal .modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #2c3e50;
}

.event-form-modal .close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.event-form-modal .close-btn:hover {
  background: #e9ecef;
  color: #495057;
}

.event-form-modal .modal-body {
  padding: 2rem;
}


/* CKEditor z-index fixes for modal */
:global(.ck.ck-balloon-panel) {
  z-index: 10000 !important;
}

:global(.ck.ck-link-actions) {
  z-index: 10001 !important;
}

:global(.ck.ck-link-form) {
  z-index: 10001 !important;
}

:global(.ck-body-wrapper) {
  z-index: 10000 !important;
}

/* Custom SweetAlert styling for centered notifications */
:global(.swal-centered-popup) {
  position: fixed !important;
  top: 20px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  width: auto !important;
  max-width: 500px !important;
  margin: 0 !important;
}

/* Responsive Modal */
@media (max-width: 768px) {
  .event-form-modal {
    width: 95vw;
    margin: 1rem;
  }
  
  .event-form-modal .modal-header,
  .event-form-modal .modal-body {
    padding: 1rem 1.5rem;
  }
  
  :global(.swal-centered-popup) {
    max-width: 90vw !important;
    top: 10px !important;
  }
}
</style>